import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type CancelToken,
} from 'axios';
import { BrowserUtility } from '../browser-utility';
import { localStorageKeys } from '../constants/storage-keys';
import { UserService } from './user';

const lamdaAPIPath = import.meta.env.VITE_API_PATH as string;
const lamdaBaseURL = lamdaAPIPath;
const apiPath = import.meta.env.VITE_API_PATH as string;

interface ErrorResponse {
  code?: string;
  error?: string;
  status?: number | string;
}

interface RefreshTokenResponse {
  accessToken: string;
  [key: string]: any;
}

const onSuccess = <T>(response: AxiosResponse<T>): T => response.data;

const onError = async (error: AxiosError): Promise<never> => {
  if (error.response) {
    if (error.response.status === 403) {
      BrowserUtility.clearExcept([localStorageKeys.packingStation]);
      window.location.href = '/';
      return Promise.reject(error);
    }
  } else {
    return Promise.reject({
      code: 'NETWORK_ERROR',
      error: 'Could not reach the server. Check your connection and try again.',
      status: '',
      details: null,
    } as ErrorResponse);
  }

  return Promise.reject({
    code: (error.response?.data as any)?.code || (error.response as any)?.code || '',
    error: (error.response?.data as any)?.error || (error.response?.data as any) || '',
    status: error.response?.status || '',
    details: (error.response?.data as any)?.details || null,
  } as ErrorResponse);
};

const refreshToken = async (error: AxiosError): Promise<AxiosRequestConfig<any>> => {
  const originalRequest = error.config as AxiosRequestConfig & {
    _retry?: boolean;
  };

  if (error?.response?.status === 403 && !originalRequest._retry) {
    originalRequest._retry = true;
    const refresh_token = UserService.getRefreshToken();

    if (refresh_token) {
      try {
        const params = { token: refresh_token };
        const result: RefreshTokenResponse = await UserService.refreshToken(params);
        UserService.storeUser(result);
        if (!originalRequest.headers) originalRequest.headers = {};
        originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
        return originalRequest;
      } catch {
        window.location.href = '/';
      }
    } else {
      window.location.href = '/';
    }
  }
  return Promise.reject(error);
};

const request = async <T = any>(
  options: AxiosRequestConfig,
  isLamdaRequest?: boolean,
  isSecure?: boolean,
  setBaseUrl?: string,
  cancelToken?: CancelToken,
  additionalConfig: AxiosRequestConfig = {},
  additionalHeaders: Record<string, string> = {},
): Promise<T> => {
  const headers: Record<string, string> = {};

  if (isSecure) {
    const token = UserService.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const client: AxiosInstance = axios.create({
    baseURL: setBaseUrl || (isLamdaRequest ? lamdaBaseURL : apiPath),
    headers: { ...headers, ...additionalHeaders },
    cancelToken,
    ...additionalConfig,
  });

  client.interceptors.response.use(
    response => response,
    async error => {
      const refreshReq = await refreshToken(error);
      return client(refreshReq);
    },
  );

  return client(options).then(onSuccess).catch(onError);
};

const uploadFiles = <T = any>(
  url: string,
  data: any,
  headers: Record<string, string> = {},
): Promise<T> => {
  return axios({
    url,
    method: 'PUT',
    headers,
    data,
  })
    .then(onSuccess)
    .catch(onError);
};

const localFiles = <T = any>(url: string): Promise<T> => {
  return axios({
    url,
    method: 'GET',
  })
    .then(onSuccess)
    .catch(onError);
};

export class BaseService {
  static get = <T = any>(
    url: string,
    isLamdaRequest = false,
    isSecure = true,
    cancelToken?: CancelToken,
    setBaseUrl?: string,
    additionalConfig?: AxiosRequestConfig,
    headers?: Record<string, string>,
  ): Promise<T> =>
    request<T>(
      { url, method: 'GET' },
      isLamdaRequest,
      isSecure,
      setBaseUrl,
      cancelToken,
      additionalConfig,
      headers,
    );

  static getWithFullResponse = <T = any>(
    url: string,
    isLamdaRequest = false,
    isSecure = true,
    cancelToken?: CancelToken,
    setBaseUrl?: string,
    additionalConfig: AxiosRequestConfig = {},
    headers: Record<string, string> = {},
  ): Promise<AxiosResponse<T>> => {
    const tokenHeaders: Record<string, string> = {};

    if (isSecure) {
      const token = UserService.getToken();
      if (token) tokenHeaders.Authorization = `Bearer ${token}`;
    }

    const client = axios.create({
      baseURL: setBaseUrl || (isLamdaRequest ? lamdaBaseURL : apiPath),
      headers: { ...tokenHeaders, ...headers },
      cancelToken,
      ...additionalConfig,
    });

    client.interceptors.response.use(
      response => response,
      async error => {
        const refreshReq = await refreshToken(error);
        return client(refreshReq);
      },
    );

    return client({ url, method: 'GET' }).catch(onError);
  };

  static post = <T = any>(
    url: string,
    data?: any,
    isLamdaRequest = false,
    isSecure = true,
    setBaseUrl?: string,
  ): Promise<T> =>
    request<T>({ url, method: 'POST', data }, isLamdaRequest, isSecure, setBaseUrl);

  static patch = <T = any>(
    url: string,
    data?: any,
    isLamdaRequest = false,
    isSecure = true,
    setBaseUrl?: string,
  ): Promise<T> =>
    request<T>({ url, method: 'PATCH', data }, isLamdaRequest, isSecure, setBaseUrl);

  static put = <T = any>(
    url: string,
    data?: any,
    isLamdaRequest = false,
    isSecure = true,
    setBaseUrl?: string,
  ): Promise<T> =>
    request<T>({ url, method: 'PUT', data }, isLamdaRequest, isSecure, setBaseUrl);

  static remove = <T = any>(
    url: string,
    isLamdaRequest = false,
    isSecure = true,
  ): Promise<T> => request<T>({ url, method: 'DELETE' }, isLamdaRequest, isSecure);

  static deleteWithBody = <T = any>(
    url: string,
    data?: any,
    isLamdaRequest = false,
    isSecure = true,
    setBaseUrl?: string,
  ): Promise<T> =>
    request<T>({ url, method: 'DELETE', data }, isLamdaRequest, isSecure, setBaseUrl);

  static upload = <T = any>(
    url: string,
    data: any,
    header?: Record<string, string>,
  ): Promise<T> => uploadFiles<T>(url, data, header);

  static localFiles = <T = any>(url: string): Promise<T> => localFiles<T>(url);
}
